import { useToast } from "@aidkitorg/component-library";
import { OAuthResponse, OAuthState } from "@aidkitorg/types/lib/auth/sso";
import * as Sentry from "@sentry/react";
import { useContext, useEffect } from "react";
import { useCookies } from "react-cookie";
import { useHistory } from "react-router-dom";
import { usePost } from "./API";
import { UserInfoContext } from "./Context";
import { safeParse, SpacedSpinner } from "./Util";
import { useLocalizedStrings } from "./Localization";

// This is used to handle the callback from Google during an OAuth flow
// Google will send a user back to tenant.aidkit.org/auth/google/callback 
// and we'll render this component to finish the last step in OAuth.
//
// Note that since wildcard redirect URIs are not allowed, we may receive
// an OAuth callback for a different program. This is expected and we'll 
// determine which tenant the user should be redirected to through the clientDomain
// supplied by OAuth state and redirect them there.
const GoogleCallback = () => {
  const L = useLocalizedStrings();
  const history = useHistory();
  const [, setCookie,] = useCookies(["auth_token", "auth_token_expires"]);
  const userContext = useContext(UserInfoContext);
  const verifyOAuthCode = usePost("/authenticate/google/verify");
  const { toast } = useToast();

  const oAuthPayload = new URLSearchParams(window.location.search)
  const oAuthCode = oAuthPayload.get('code');
  const oAuthState = oAuthPayload.get('state');

  useEffect(() => {
    const oAuthParsedState: OAuthState = oAuthState ? safeParse(atob(oAuthState), {}) : {};

    // Early return if there is no OAuth code or state 
    // OR if we weren't provided a clientDomain which we need to 
    // redirect the user to the right tenant.
    if (!oAuthCode || !oAuthState || !oAuthParsedState.clientDomain) {
      history.push("/login");
      toast({
        title: "SSO Error",
        description: "Error authenticating",
        variant: "error"
      })
      return;
    }

    // Redirect to the correct tenant based on the state payload
    if (window.location.origin !== oAuthParsedState?.clientDomain) {
      window.location.href = `${oAuthParsedState.clientDomain}/auth/google/callback?code=${oAuthCode}&state=${oAuthState}`;
      return;
    }

    // Validate the oauth token from Google and authenticate
    const validateToken = async () => {
      const result: OAuthResponse = await verifyOAuthCode({
        code: oAuthCode,
        state: oAuthState || ''
      });

      // If verification failed or state mismatch, inform opener (if any), then close/fallback
      const expectedState = sessionStorage.getItem('oauth_state_token');
      const targetOrigin = new URL(oAuthParsedState.clientDomain!).origin;
   
      const fail = (message = 'Authentication error') => {
        if (window.opener && !window.opener.closed) {
          try {
            window.opener.postMessage({ type: 'SSO_ERROR', message }, targetOrigin);
          } catch {}
          window.close();
          return;
        }
        // non-popup fallback
        sessionStorage.removeItem('oauth_state_token');
        history.push("/login");
        toast({ title: "SSO Error", description: message, variant: "error" });
      };

      if (!result || 'error' in result || !result.authToken || result.oAuthStateToken !== sessionStorage.getItem('oauth_state_token')) {
        fail();
        return;
      }

      // If we were opened as a popup, send the token to the opener and close
      if (window.opener && !window.opener.closed) {
        try {
          window.opener.postMessage(
            { type: 'SSO_SUCCESS', token: result.authToken, state: result.oAuthStateToken },
            targetOrigin
          );
        } catch {
          // If cross-origin or other error, at least try to tell the opener
          try { window.opener.postMessage({ type: 'SSO_ERROR', message: 'postMessage failed' }, targetOrigin); } catch {}
        }
        // Give the message a brief moment to dispatch, then close
        setTimeout(() => window.close(), 50);
        return;
      }


      // Create auth token that self-expires after 24 hours
      const TOKEN_LIFETIME_SECONDS = 86400; // 24 hours in seconds
      setCookie("auth_token", result.authToken, {
        maxAge: TOKEN_LIFETIME_SECONDS, 
        path: "/",
        sameSite: "strict"
      });
      setCookie("auth_token_expires", Date.now() + TOKEN_LIFETIME_SECONDS * 1000, {
        maxAge: TOKEN_LIFETIME_SECONDS,
        path: "/",
        sameSite: "strict"
      });

      // Causes the UserInfoWrapper which provides UserInfoContext to call /user/info again now that we have a verified token
      if (userContext?.refreshContext) {
        userContext.refreshContext();
      } else {
        Sentry.captureMessage('Could not refresh UserInfoContext, callback missing', 'warning');
      }

      // Remove temp oauth state token after successful auth
      sessionStorage.removeItem('oauth_state_token');

      // Find the place they came in on.
      if (window.opener && !window.opener.closed) {
        window.close();
      } else {
        history.push(result.next || '/');
      }
    }
    validateToken();
  }, []);

  return (
    <div className="flex justify-center items-center h-screen">
      <div className="sr-only">{L.applicant.loading}</div>
      <SpacedSpinner />
    </div>
  );
}

export default GoogleCallback;
